import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Link } from 'react-router-dom';
import { Archive, ArrowLeftRight, CalendarOff, Check, Plus } from 'lucide-react';
import PortalBackground from '../../components/PortalBackground';

const departments = ['Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR', 'General'];

const deptStyles = {
  Sales: 'bg-red-100 text-red-800',
  Operations: 'bg-blue-100 text-blue-800',
  Finance: 'bg-green-100 text-green-800',
  Technology: 'bg-purple-100 text-purple-800',
  Marketing: 'bg-orange-100 text-orange-800',
  HR: 'bg-yellow-100 text-yellow-800',
  General: 'bg-gray-100 text-gray-800',
};

const cardBackgroundStyles = {
  Sales: 'bg-red-100 dark:bg-red-900/30',
  Operations: 'bg-blue-100 dark:bg-blue-900/30',
  Finance: 'bg-green-100 dark:bg-green-900/30',
  Technology: 'bg-purple-100 dark:bg-purple-900/30',
  Marketing: 'bg-orange-100 dark:bg-orange-900/30',
  HR: 'bg-yellow-100 dark:bg-yellow-900/30',
  General: 'bg-gray-100 dark:bg-gray-800',
};

const cardBorderStyles = {
  Sales: 'border-red-400 dark:border-red-500',
  Operations: 'border-blue-400 dark:border-blue-500',
  Finance: 'border-green-400 dark:border-green-500',
  Technology: 'border-purple-400 dark:border-purple-500',
  Marketing: 'border-orange-400 dark:border-orange-500',
  HR: 'border-yellow-400 dark:border-yellow-500',
  General: 'border-gray-300 dark:border-gray-600',
};

const avatarStyles = [
  'bg-gradient-to-br from-red-500 to-pink-500',
  'bg-gradient-to-br from-purple-500 to-indigo-500',
  'bg-gradient-to-br from-orange-500 to-amber-500',
  'bg-gradient-to-br from-teal-500 to-cyan-500',
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Firestore value when presenter is an external guest (not internal_users). */
const GUEST_PRESENTER_ID = '__guest_presenter__';

const getStartOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const getIsoWeekNumber = (date) => {
  const temp = new Date(date);
  temp.setHours(0, 0, 0, 0);
  temp.setDate(temp.getDate() + 3 - ((temp.getDay() + 6) % 7));
  const week1 = new Date(temp.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((temp.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) /
        7
    )
  );
};

const formatShortDate = (date) =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const dateToISO = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const pickProfilePicUrl = (data) => {
  if (!data) return '';
  const candidates = [
    data.profileImage,
    data.profilePicture,
    data.photoURL,
    data.photoUrl,
    data.avatarUrl,
    data.avatarURL,
    data.imageUrl,
    data.imageURL,
    data.picture,
    data.profilePic,
  ];
  const url = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  return url ? url.trim() : '';
};

const sessionFromDoc = (docSnap) => {
  const data = docSnap.data();
  const dateObj = data.date?.toDate ? data.date.toDate() : new Date(data.date);
  return {
    id: docSnap.id,
    date: dateObj,
    isoDate: dateToISO(dateObj),
    eventName: data.eventName || 'Breakthrough Session',
    presenter: data.presenter || '',
    presenterId: data.presenterId || '',
    presenterPhoto: data.presenterPhoto || '',
    presenterInitials: data.presenterInitials || '',
    status: data.status || 'active',
    department: data.department || 'General',
    description: data.description || '',
    startTime: data.startTime || '',
    endTime: data.endTime || '',
    needPresenter: data.needPresenter || false,
    notHeld: data.notHeld === true,
    attachments: Array.isArray(data.attachments) ? data.attachments : [],
    archived: data.archived === true,
    archivedAt: data.archivedAt?.toDate ? data.archivedAt.toDate() : data.archivedAt,
  };
};

const emptySession = (date, label = '') => ({
  id: `empty-${dateToISO(date)}`,
  date,
  isoDate: dateToISO(date),
  eventName: 'Not scheduled',
  presenter: '',
  presenterId: '',
  presenterPhoto: '',
  presenterInitials: '',
  status: 'draft',
  department: 'General',
  description: '',
  startTime: '',
  endTime: '',
  attachments: [],
  isEmpty: true,
  label: label || date.toLocaleDateString(undefined, { weekday: 'long' }),
});

// Get the 4 Wednesdays for a given month (1-indexed)
const getWednesdaysForMonth = (year, month) => {
  const wednesdays = [];
  const first = new Date(year, month - 1, 1);
  let day = first.getDay();
  const daysUntilWed = (3 - day + 7) % 7;
  let wed = first.getDate() + daysUntilWed;
  if (daysUntilWed === 0 && day !== 3) wed += 7;
  for (let i = 0; i < 4; i++) {
    const d = new Date(year, month - 1, wed + i * 7);
    if (d.getMonth() === month - 1) wednesdays.push(d);
  }
  return wednesdays;
};

// Get default date for adding a new session to a month (5th Wed if exists, else last day)
const getDefaultDateForNewSession = (year, month) => {
  const wednesdays = getWednesdaysForMonth(year, month);
  const fifthWed = wednesdays.length === 4 ? new Date(year, month - 1, wednesdays[3].getDate() + 7) : null;
  if (fifthWed && fifthWed.getMonth() === month - 1) return fifthWed;
  return new Date(year, month, 0); // last day of month
};

const BreakthroughsFulfillment = () => {
  const [selectedSession, setSelectedSession] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(null);
  const [sessionsByDate, setSessionsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [internalUsers, setInternalUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());
  const [archiveConfirmMonth, setArchiveConfirmMonth] = useState(null);
  const [extendedMonths, setExtendedMonths] = useState(new Set());
  const [swapMode, setSwapMode] = useState(false);
  const [swapSelection, setSwapSelection] = useState([]);
  const [swapping, setSwapping] = useState(false);

  const guestPendingPhotoFile = formData?.pendingPresenterPhoto;
  const guestPendingPhotoPreviewUrl = useMemo(() => {
    if (!guestPendingPhotoFile) return null;
    return URL.createObjectURL(guestPendingPhotoFile);
  }, [guestPendingPhotoFile]);

  useEffect(() => {
    return () => {
      if (guestPendingPhotoPreviewUrl) URL.revokeObjectURL(guestPendingPhotoPreviewUrl);
    };
  }, [guestPendingPhotoPreviewUrl]);

  const toggleMonth = (key) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const year = new Date().getFullYear();
        const startBoundary = Timestamp.fromDate(new Date(year, 0, 1));
        const endOfNextYear = new Date(year + 1, 11, 31, 23, 59, 59).getTime();
        const q = query(
          collection(db, 'breakthroughSessions'),
          where('date', '>=', startBoundary),
          orderBy('date', 'asc')
        );
        const snap = await getDocs(q);
        const map = {};
        snap.forEach((docSnap) => {
          const session = sessionFromDoc(docSnap);
          if (session.archived) return;
          if (new Date(session.date).getTime() <= endOfNextYear) {
            map[session.id] = session;
          }
        });
        setSessionsByDate(map);
      } catch (err) {
        console.error('Failed to load sessions', err);
        setLoadError('Failed to load sessions. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const internalUsersRef = collection(db, 'internal_users');
        const snap = await getDocs(internalUsersRef);
        const list = snap.docs.map((docSnap) => {
          const data = docSnap.data();
          let displayName = 'Unknown User';
          if (data.firstName && data.lastName) {
            displayName = `${data.firstName} ${data.lastName}`;
          } else if (data.displayName) {
            displayName = data.displayName;
          } else if (data.name) {
            displayName = data.name;
          } else if (data.email) {
            displayName = data.email;
          }
          return {
            id: docSnap.id,
            name: displayName,
            email: data.email || '',
            photoURL: pickProfilePicUrl(data),
            initials:
              (data.firstName && data.lastName
                ? `${data.firstName[0]}${data.lastName[0]}`
                : displayName
              )
                .slice(0, 2)
                .toUpperCase(),
          };
        });
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setInternalUsers(list);
      } catch (err) {
        console.error('Failed to load users', err);
        setUsersError('Unable to load presenters list.');
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const year = new Date().getFullYear();

  const getNextMonthKey = (key) => {
    const [y, m] = key.split('-').map(Number);
    if (m === 12) return `${y + 1}-01`;
    return `${y}-${String(m + 1).padStart(2, '0')}`;
  };

  const addNewMonth = () => {
    const lastKey = monthRows.length > 0 ? monthRows[monthRows.length - 1].key : null;
    const nextKey = lastKey
      ? getNextMonthKey(lastKey)
      : (() => {
          const now = new Date();
          const currM = now.getMonth() + 1;
          const nextM = currM === 12 ? 1 : currM + 1;
          const nextY = currM === 12 ? now.getFullYear() + 1 : now.getFullYear();
          return `${nextY}-${String(nextM).padStart(2, '0')}`;
        })();
    setExtendedMonths((prev) => new Set([...prev, nextKey]));
  };

  // Build month rows: months with sessions + extended months (user-added), sorted by date
  const monthRows = useMemo(() => {
    const byMonth = {};
    Object.values(sessionsByDate).forEach((session) => {
      const key = session.isoDate ? session.isoDate.substring(0, 7) : `${new Date(session.date).getFullYear()}-${String(new Date(session.date).getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = [];
      byMonth[key].push(session);
    });
    Object.keys(byMonth).forEach((k) => {
      byMonth[k].sort((a, b) => new Date(a.date) - new Date(b.date));
    });

    const allKeys = new Set([...Object.keys(byMonth), ...extendedMonths]);
    const rows = Array.from(allKeys)
      .sort()
      .map((key) => {
        const [y, m] = key.split('-').map(Number);
        const sessions = (byMonth[key] || []).slice();
        sessions.sort((a, b) => new Date(a.date) - new Date(b.date));
        return {
          key,
          label: `${MONTH_NAMES[m - 1]} ${y}`,
          slots: sessions,
          monthNum: m,
          yearNum: y,
        };
      });
    return rows;
  }, [year, sessionsByDate, extendedMonths]);

  const initialsFor = (presenter, fallback) => {
    if (!presenter) return fallback;
    return presenter
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const resolvePresenterPhoto = (sessionLike) => {
    if (!sessionLike) return '';
    if (sessionLike.presenterPhoto) return sessionLike.presenterPhoto;
    if (!sessionLike.presenterId || sessionLike.presenterId === GUEST_PRESENTER_ID) return '';
    const match = internalUsers.find((u) => u.id === sessionLike.presenterId);
    return match?.photoURL || '';
  };

  const openSession = (session) => {
    setSelectedSession(session);
    setEditMode(false);
    setFormData(null);
    setSaveError(null);
  };

  const startEditing = (session) => {
    setEditMode(true);
    const isGuestPresenter =
      session.presenterId === GUEST_PRESENTER_ID ||
      (!session.presenterId && !!(session.presenter || '').trim());
    setFormData({
      eventName: session.eventName === 'Not scheduled' ? 'Breakthrough Session' : session.eventName,
      presenter: session.presenter || '',
      presenterId: isGuestPresenter ? GUEST_PRESENTER_ID : session.presenterId || '',
      presenterPhoto: session.presenterPhoto || '',
      presenterInitials: session.presenterInitials || '',
      department: session.department || 'General',
      description: session.description || '',
      date: dateToISO(session.date),
      startTime: session.startTime || '13:00',
      endTime: session.endTime || '14:00',
      needPresenter: session.needPresenter || false,
      attachments: session.attachments ? [...session.attachments] : [],
      pendingFiles: [],
      pendingPresenterPhoto: null,
    });
  };

  const handleFileSelect = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      pendingFiles: [...(prev.pendingFiles || []), ...files],
    }));
    e.target.value = '';
  };

  const removePendingFile = (idx) => {
    setFormData((prev) => ({
      ...prev,
      pendingFiles: (prev.pendingFiles || []).filter((_, i) => i !== idx),
    }));
  };

  const removeAttachment = (idx) => {
    setFormData((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, i) => i !== idx),
    }));
  };

  const toggleNotHeld = async () => {
    if (!selectedSession || selectedSession.isEmpty) return;
    const next = !selectedSession.notHeld;
    setSaveError(null);
    try {
      await updateDoc(doc(db, 'breakthroughSessions', selectedSession.id), {
        notHeld: next,
        updatedAt: serverTimestamp(),
      });
      const updated = { ...selectedSession, notHeld: next };
      setSelectedSession(updated);
      setSessionsByDate((prev) => ({ ...prev, [updated.id]: updated }));
    } catch (err) {
      console.error('Failed to update session', err);
      setSaveError('Failed to update. Please try again.');
    }
  };

  const archiveMonth = async (monthKey) => {
    const sessionsToArchive = Object.values(sessionsByDate).filter((s) => {
      const key = s.isoDate ? s.isoDate.substring(0, 7) : `${new Date(s.date).getFullYear()}-${String(new Date(s.date).getMonth() + 1).padStart(2, '0')}`;
      return key === monthKey;
    });
    setArchiveConfirmMonth(null);
    if (sessionsToArchive.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const batch = sessionsToArchive.map((s) =>
        updateDoc(doc(db, 'breakthroughSessions', s.id), {
          archived: true,
          archivedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      );
      await Promise.all(batch);
      setSessionsByDate((prev) => {
        const next = { ...prev };
        sessionsToArchive.forEach((s) => delete next[s.id]);
        return next;
      });
    } catch (err) {
      console.error('Failed to archive', err);
      setSaveError('Failed to archive. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const exitSwapMode = () => {
    setSwapMode(false);
    setSwapSelection([]);
  };

  const toggleSwapSelection = (session) => {
    if (session.isEmpty) return;
    setSwapSelection((prev) => {
      if (prev.includes(session.id)) return prev.filter((id) => id !== session.id);
      if (prev.length >= 2) return [prev[1], session.id];
      return [...prev, session.id];
    });
  };

  // Swap only the dates of two sessions; presenter, times, topic, etc. stay with each session.
  const swapSessionDates = async () => {
    if (swapSelection.length !== 2) return;
    const [a, b] = swapSelection.map((id) => sessionsByDate[id]);
    if (!a || !b) return;
    setSwapping(true);
    setSaveError(null);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const buildDateUpdate = (dateValue) => {
      const d = new Date(dateValue);
      d.setHours(0, 0, 0, 0);
      const weekStart = getStartOfWeek(d);
      return {
        date: d,
        payload: {
          date: Timestamp.fromDate(d),
          dayOfWeek: dayNames[d.getDay()],
          weekStart: Timestamp.fromDate(weekStart),
          weekNumber: getIsoWeekNumber(weekStart),
          updatedAt: serverTimestamp(),
        },
      };
    };
    const forA = buildDateUpdate(b.date);
    const forB = buildDateUpdate(a.date);
    try {
      await Promise.all([
        updateDoc(doc(db, 'breakthroughSessions', a.id), forA.payload),
        updateDoc(doc(db, 'breakthroughSessions', b.id), forB.payload),
      ]);
      const withDate = (session, d) => ({
        ...session,
        date: d,
        isoDate: dateToISO(d),
        label: d.toLocaleDateString(undefined, { weekday: 'long' }),
      });
      setSessionsByDate((prev) => ({
        ...prev,
        [a.id]: withDate(a, forA.date),
        [b.id]: withDate(b, forB.date),
      }));
      exitSwapMode();
    } catch (err) {
      console.error('Failed to swap sessions', err);
      setSaveError('Failed to swap dates. Please try again.');
    } finally {
      setSwapping(false);
    }
  };

  const uploadFileToSession = async (file, sessionId) => {
    const sanitized = (file.name || 'file').replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `breakthrough-sessions/${sessionId}/${Date.now()}-${sanitized}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const fileUrl = await getDownloadURL(storageRef);
    return { fileName: file.name, fileUrl, storagePath, uploadDate: new Date().toISOString() };
  };

  const uploadGuestPresenterImage = async (file, sessionId) => {
    const ext = (file.name && file.name.includes('.') ? file.name.split('.').pop() : 'jpg').replace(/[^a-zA-Z0-9]/g, '') || 'jpg';
    const storagePath = `breakthrough-sessions/${sessionId}/presenter-photo-${Date.now()}.${ext}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const saveSession = async () => {
    if (!selectedSession) return;
    setSaving(true);
    setSaveError(null);
    try {
      const isGuestPresenter = formData?.presenterId === GUEST_PRESENTER_ID;
      const selectedPresenter =
        formData?.presenterId && !isGuestPresenter
          ? internalUsers.find((u) => u.id === formData.presenterId)
          : null;
      let sessionDate;
      if (formData.date) {
        const [y, mo, d] = formData.date.split('-').map(Number);
        sessionDate = new Date(y, mo - 1, d);
        sessionDate.setHours(0, 0, 0, 0);
      } else {
        sessionDate = selectedSession.date;
      }
      const weekStart = getStartOfWeek(sessionDate);
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const guestNameTrimmed = isGuestPresenter ? (formData.presenter || '').trim() : '';
      const guestInitials = isGuestPresenter ? initialsFor(guestNameTrimmed, 'GU') : '';
      const basePayload = {
        eventName: formData.eventName || 'Breakthrough Session',
        presenter: isGuestPresenter ? guestNameTrimmed : formData.presenter || '',
        presenterId: isGuestPresenter ? GUEST_PRESENTER_ID : formData.presenterId || '',
        presenterPhoto: selectedPresenter?.photoURL || formData.presenterPhoto || '',
        presenterInitials: isGuestPresenter ? guestInitials : formData.presenterInitials || '',
        dayOfWeek: dayNames[sessionDate.getDay()],
        department: formData.department || 'General',
        description: formData.description || '',
        startTime: formData.startTime || '',
        endTime: formData.endTime || '',
        needPresenter: formData.needPresenter || false,
        date: Timestamp.fromDate(sessionDate),
        weekStart: Timestamp.fromDate(weekStart),
        weekNumber: getIsoWeekNumber(weekStart),
        status: 'active',
        updatedAt: serverTimestamp(),
      };

      let sessionId;
      let attachments = [...(formData.attachments || [])];

      if (selectedSession.isEmpty) {
        const docRef = await addDoc(collection(db, 'breakthroughSessions'), {
          ...basePayload,
          attachments: [],
          createdAt: serverTimestamp(),
        });
        sessionId = docRef.id;
      } else {
        sessionId = selectedSession.id;
      }

      const pendingFiles = formData.pendingFiles || [];
      for (const file of pendingFiles) {
        const att = await uploadFileToSession(file, sessionId);
        attachments.push(att);
      }

      let presenterPhotoFinal = basePayload.presenterPhoto;
      if (isGuestPresenter && formData.pendingPresenterPhoto) {
        presenterPhotoFinal = await uploadGuestPresenterImage(formData.pendingPresenterPhoto, sessionId);
      }

      const payload = {
        ...basePayload,
        attachments,
        presenterPhoto: presenterPhotoFinal,
        presenterInitials: isGuestPresenter ? guestInitials : basePayload.presenterInitials,
      };

      if (selectedSession.isEmpty) {
        await updateDoc(doc(db, 'breakthroughSessions', sessionId), payload);
        const updatedSession = {
          ...basePayload,
          attachments,
          id: sessionId,
          date: sessionDate,
          isoDate: dateToISO(sessionDate),
          label: sessionDate.toLocaleDateString(undefined, { weekday: 'long' }),
          isEmpty: false,
        };
        setSessionsByDate((prev) => ({ ...prev, [updatedSession.id]: updatedSession }));
        setSelectedSession(updatedSession);
      } else {
        await updateDoc(doc(db, 'breakthroughSessions', selectedSession.id), payload);
        const updatedSession = {
          ...selectedSession,
          ...payload,
          date: sessionDate,
          isoDate: dateToISO(sessionDate),
          label: sessionDate.toLocaleDateString(undefined, { weekday: 'long' }),
          isEmpty: false,
        };
        setSessionsByDate((prev) => ({ ...prev, [updatedSession.id]: updatedSession }));
        setSelectedSession(updatedSession);
      }
      setEditMode(false);
    } catch (err) {
      console.error('Failed to save session', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (session) => {
    const isNeedPresenter = session.needPresenter;
    const isPlaceholder = session.isEmpty;
    const deptClass = deptStyles[session.department] || 'bg-gray-100 text-gray-800';
    const cardBgClass = isPlaceholder
      ? 'bg-gray-50 dark:bg-gray-800/50 border-dashed'
      : isNeedPresenter
        ? 'bg-white dark:bg-gray-800'
        : (cardBackgroundStyles[session.department] || 'bg-gray-100 dark:bg-gray-800');
    const cardBorderClass = isPlaceholder
      ? 'border-gray-300 dark:border-gray-600'
      : isNeedPresenter
        ? 'border-gray-400 dark:border-gray-500'
        : (cardBorderStyles[session.department] || 'border-gray-300 dark:border-gray-600');
    const textColorClass = isNeedPresenter ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100';
    const avatarIndex =
      session.presenter && session.presenter.length > 0
        ? Math.abs(session.presenter.charCodeAt(0)) % avatarStyles.length
        : new Date(session.date).getMonth() % avatarStyles.length;
    const avatarClass = avatarStyles[avatarIndex];
    const initials = session.presenterInitials || initialsFor(session.presenter, session.label?.slice(0, 2).toUpperCase() || '—');
    const presenterPhoto = resolvePresenterPhoto(session);
    const swapIndex = swapSelection.indexOf(session.id);
    const isSwapSelected = swapIndex !== -1;
    const swapStateClass = swapMode
      ? isPlaceholder
        ? 'opacity-40 cursor-not-allowed'
        : isSwapSelected
          ? 'ring-4 ring-red-500 ring-offset-2 dark:ring-offset-gray-900'
          : 'hover:ring-2 hover:ring-red-300'
      : '';

    return (
      <div
        key={session.id}
        className={`relative border-2 ${cardBorderClass} rounded-lg p-4 flex flex-col gap-3 cursor-pointer hover:shadow-md transition-shadow ${cardBgClass} ${swapStateClass}`}
        onClick={() => (swapMode ? toggleSwapSelection(session) : openSession(session))}
      >
        {isSwapSelected && (
          <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow">
            {swapIndex + 1}
          </span>
        )}
        <div className="flex items-center gap-3">
          {presenterPhoto ? (
            <img
              src={presenterPhoto}
              alt={session.presenter || 'Presenter'}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div
              className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold ${avatarClass}`}
            >
              {initials}
            </div>
          )}
          <div>
            <div className="text-sm text-gray-500 dark:text-gray-300">{session.label}</div>
            <div className="text-sm text-gray-700 dark:text-gray-200">
              {formatShortDate(session.date)}
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          {isPlaceholder ? (
            <div className="text-gray-500 dark:text-gray-400 text-center py-2">
              <span className="text-sm">Click to add session</span>
            </div>
          ) : isNeedPresenter ? (
            <>
              <div className={`text-lg font-semibold ${textColorClass} flex items-center gap-2`}>
                <span>⚠️</span>
                <span>Need Presenter</span>
              </div>
              <div className={`text-sm ${textColorClass} mt-1`}>
                {formatShortDate(session.date)} • {session.startTime || ''} {session.startTime && session.endTime ? '-' : ''} {session.endTime || ''}
              </div>
            </>
          ) : (
            <>
              {session.notHeld && (
                <span className="inline-flex items-center gap-1 self-start px-2 py-1 mb-1 rounded-full text-xs font-semibold bg-red-600 text-white">
                  <CalendarOff className="h-3 w-3" />
                  Not holding
                </span>
              )}
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {session.eventName}
              </div>
              <span
                className={`inline-flex self-start px-2 py-1 mt-1 rounded-full text-xs font-medium ${deptClass}`}
              >
                {session.department || 'Unassigned'}
              </span>
              <div className="text-sm text-gray-700 dark:text-gray-200 mt-2">
                {session.presenter ? `Presenter: ${session.presenter}` : 'No presenter yet'}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-[1600px] mx-auto">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 mb-6">
          <Link
            to="/crm/fulfillment"
            aria-label="Back to Fulfillment"
            className="justify-self-start inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            ← Back
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white text-center">
            Breakthrough Sessions Fulfillment
          </h1>
          <div className="justify-self-end flex items-center gap-2">
            <button
              type="button"
              onClick={() => (swapMode ? exitSwapMode() : setSwapMode(true))}
              aria-pressed={swapMode}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                swapMode
                  ? 'border-red-600 bg-red-600 text-white hover:bg-red-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800'
              }`}
            >
              <ArrowLeftRight className="h-4 w-4" />
              Swap
            </button>
            <Link
              to="/crm/fulfillment/breakthroughsessionsarchive"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              aria-label="View archive"
            >
              <Archive className="h-4 w-4" />
              Archive
            </Link>
          </div>
        </div>

        {swapMode && (() => {
          const picked = swapSelection.map((id) => sessionsByDate[id]).filter(Boolean);
          const describe = (s) =>
            `${s.eventName}${s.presenter ? ` (${s.presenter})` : ''} — ${formatShortDate(s.date)}`;
          return (
            <div className="sticky top-4 z-40 mb-6 rounded-xl border border-red-200 dark:border-red-900 bg-white/95 dark:bg-gray-900/95 backdrop-blur shadow-lg px-4 py-3 flex flex-wrap items-center justify-center gap-3 text-center">
              <ArrowLeftRight className="h-5 w-5 text-red-600 shrink-0" />
              <div className="text-sm text-gray-800 dark:text-gray-100">
                {picked.length === 0 && 'Select two sessions to swap their dates.'}
                {picked.length === 1 && (
                  <>
                    <span className="font-semibold">{describe(picked[0])}</span>
                    <span className="text-gray-500 dark:text-gray-400"> — now pick the second session.</span>
                  </>
                )}
                {picked.length === 2 && (
                  <>
                    <span className="font-semibold">{describe(picked[0])}</span>
                    <span className="mx-2 text-red-600">⇄</span>
                    <span className="font-semibold">{describe(picked[1])}</span>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={exitSwapMode}
                  className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={swapSessionDates}
                  disabled={picked.length !== 2 || swapping}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="h-4 w-4" />
                  {swapping ? 'Swapping…' : 'Swap Dates'}
                </button>
              </div>
            </div>
          );
        })()}

        {loading && (
          <div className="text-center text-gray-600 dark:text-gray-300 mb-4">Loading schedule…</div>
        )}
        {loadError && (
          <div className="text-center text-sm text-red-600 dark:text-red-400 mb-4">{loadError}</div>
        )}
        {saveError && (
          <div className="text-center text-sm text-red-600 dark:text-red-400 mb-4">{saveError}</div>
        )}

        <div className="space-y-10">
          {monthRows.map(({ key, label, slots, monthNum, yearNum }) => {
            const isCollapsed = collapsedMonths.has(key);
            return (
              <div key={key}>
                <div className="flex items-center gap-2 mb-4">
                  <h2
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleMonth(key)}
                    onKeyDown={(e) => e.key === 'Enter' && toggleMonth(key)}
                    className="text-xl font-semibold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-red-600 dark:hover:text-red-400 select-none flex items-center gap-2"
                  >
                    <span className="text-sm text-gray-500">{isCollapsed ? '▶' : '▼'}</span>
                    {label}
                  </h2>
                  {isCollapsed ? (
                    slots.length > 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setArchiveConfirmMonth({ key, label, slots });
                        }}
                        className="p-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                        aria-label={`Archive ${label}`}
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                    ) : null
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const sessionDate = getDefaultDateForNewSession(yearNum ?? year, monthNum);
                        sessionDate.setHours(0, 0, 0, 0);
                        const newSession = emptySession(
                          sessionDate,
                          sessionDate.toLocaleDateString(undefined, { weekday: 'long' })
                        );
                        setSelectedSession(newSession);
                        setEditMode(true);
                        setFormData({
                          eventName: 'Breakthrough Session',
                          presenter: '',
                          presenterId: '',
                          presenterPhoto: '',
                          presenterInitials: '',
                          department: 'General',
                          description: '',
                          date: dateToISO(sessionDate),
                        startTime: '13:00',
                        endTime: '14:00',
                          needPresenter: false,
                          attachments: [],
                          pendingFiles: [],
                          pendingPresenterPhoto: null,
                        });
                        setSaveError(null);
                      }}
                      className="p-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 text-sm font-medium"
                      aria-label={`Add session to ${label}`}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {!isCollapsed && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {slots.map((session) => renderCard(session))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={addNewMonth}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-gray-300 text-gray-600 hover:border-red-500 hover:text-red-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-red-500 dark:hover:text-red-400 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add new month/year
            </button>
          </div>
        </div>
      </div>

      {archiveConfirmMonth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setArchiveConfirmMonth(null)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Archive {archiveConfirmMonth.label}?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              This will move {archiveConfirmMonth.slots.length} session{archiveConfirmMonth.slots.length !== 1 ? 's' : ''} to the archive. You can view them in the Archive.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setArchiveConfirmMonth(null)}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => archiveMonth(archiveConfirmMonth.key)}
                disabled={saving}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {saving ? 'Archiving...' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setSelectedSession(null);
              setEditMode(false);
              setFormData(null);
              setSaveError(null);
            }}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-2xl w-full p-6">
            {!selectedSession.isEmpty && (
              <button
                type="button"
                onClick={toggleNotHeld}
                title={
                  selectedSession.notHeld
                    ? 'Not holding this session — click to restore'
                    : 'Mark as not holding this week'
                }
                aria-label={
                  selectedSession.notHeld
                    ? 'Not holding this session — click to restore'
                    : 'Mark as not holding this week'
                }
                className={`absolute left-4 top-4 p-1.5 rounded-md transition-colors ${
                  selectedSession.notHeld
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30'
                }`}
              >
                <CalendarOff className="h-5 w-5" />
              </button>
            )}
            <div className="flex items-start justify-between mb-4">
              <div className="text-center w-full">
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {editMode ? 'Edit Session' : selectedSession.eventName}
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedSession(null);
                  setEditMode(false);
                  setFormData(null);
                  setSaveError(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 absolute right-4 top-4"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            {!editMode && (
              <>
                <div className="flex items-center gap-3 mb-4 justify-center">
                  {resolvePresenterPhoto(selectedSession) ? (
                    <img
                      src={resolvePresenterPhoto(selectedSession)}
                      alt={selectedSession.presenter || 'Presenter'}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold ${
                        avatarStyles[
                          (selectedSession.presenter || '').charCodeAt?.(0)
                            ? Math.abs(selectedSession.presenter.charCodeAt(0)) % avatarStyles.length
                            : (selectedSession.weekNumber || 0) % avatarStyles.length
                        ]
                      }`}
                    >
                      {selectedSession.presenterInitials ||
                        initialsFor(selectedSession.presenter, selectedSession.label?.slice(0, 2) || '—')}
                    </div>
                  )}
                  <div className="text-left">
                    <div className="text-sm text-gray-500 dark:text-gray-300">Presenter</div>
                    <div className="text-base text-gray-900 dark:text-gray-100">
                      {selectedSession.presenter || 'Not set'}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-800 dark:text-gray-100">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Date</span>
                    <span>{formatShortDate(selectedSession.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Time</span>
                    <span>
                      {selectedSession.startTime || '—'} {selectedSession.startTime && ' - '} {selectedSession.endTime}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-300">Department</span>
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        deptStyles[selectedSession.department] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {selectedSession.department || 'Unassigned'}
                    </span>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-300 mb-1">Description</div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {selectedSession.description || 'No description yet.'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-300 mb-2">Attachments</div>
                    {selectedSession.attachments && selectedSession.attachments.length > 0 ? (
                      <div className="space-y-2">
                        {selectedSession.attachments.map((att, idx) => (
                          <div
                            key={`${selectedSession.id}-att-${idx}`}
                            className="flex items-center justify-between gap-2 py-1.5 px-2 rounded bg-gray-100 dark:bg-gray-800"
                          >
                            <a
                              href={att.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-red-600 dark:text-red-400 hover:underline truncate flex-1"
                            >
                              {att.fileName || 'Download'}
                            </a>
                            <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                              {att.uploadDate ? new Date(att.uploadDate).toLocaleDateString() : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600 dark:text-gray-300">No attachments.</div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-between items-center">
                  <button
                    onClick={() => startEditing(selectedSession)}
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {selectedSession.isEmpty ? 'Add Session' : 'Edit Session'}
                  </button>
                  <div className="flex gap-2">
                    {!selectedSession.isEmpty && (
                      <button
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to delete this session?')) return;
                          try {
                            await deleteDoc(doc(db, 'breakthroughSessions', selectedSession.id));
                            setSessionsByDate((prev) => {
                              const next = { ...prev };
                              delete next[selectedSession.id];
                              return next;
                            });
                            setSelectedSession(null);
                            setEditMode(false);
                            setFormData(null);
                          } catch (err) {
                            console.error('Delete failed', err);
                            setSaveError('Failed to delete. Please try again.');
                          }
                        }}
                        className="px-4 py-2 rounded-md border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-200 dark:hover:bg-red-900/30"
                      >
                        Delete
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedSession(null);
                        setEditMode(false);
                        setFormData(null);
                      }}
                      className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            )}

            {editMode && formData && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Event Name</label>
                    <input
                      className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                      value={formData.eventName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, eventName: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Presenter</label>
                    <select
                      className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100"
                      value={
                        formData.presenterId === GUEST_PRESENTER_ID
                          ? GUEST_PRESENTER_ID
                          : formData.presenterId || ''
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === GUEST_PRESENTER_ID) {
                          setFormData((prev) => ({
                            ...prev,
                            presenterId: GUEST_PRESENTER_ID,
                            presenter: '',
                            presenterPhoto: '',
                            presenterInitials: 'GU',
                            pendingPresenterPhoto: null,
                          }));
                          return;
                        }
                        const user = internalUsers.find((u) => u.id === val);
                        setFormData((prev) => ({
                          ...prev,
                          presenterId: user?.id || '',
                          presenter: user?.name || '',
                          presenterPhoto: user?.photoURL || '',
                          presenterInitials: user?.initials || initialsFor(user?.name, 'PR'),
                          pendingPresenterPhoto: null,
                        }));
                      }}
                    >
                      <option value="">Select presenter</option>
                      {internalUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                      <option value={GUEST_PRESENTER_ID}>Guest</option>
                    </select>
                    {formData.presenterId === GUEST_PRESENTER_ID && (
                      <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                            Guest name
                          </label>
                          <input
                            type="text"
                            className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100"
                            placeholder="Full name"
                            value={formData.presenter}
                            onChange={(e) => {
                              const name = e.target.value;
                              setFormData((prev) => ({
                                ...prev,
                                presenter: name,
                                presenterInitials: initialsFor(name.trim(), 'GU'),
                              }));
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                            Guest profile image
                          </label>
                          <div className="flex flex-wrap items-center gap-3">
                            {(guestPendingPhotoPreviewUrl || formData.presenterPhoto) && (
                              <img
                                src={guestPendingPhotoPreviewUrl || formData.presenterPhoto}
                                alt=""
                                className="h-14 w-14 rounded-full object-cover border border-gray-200 dark:border-gray-600"
                              />
                            )}
                            <label className="cursor-pointer px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                              {formData.presenterPhoto || formData.pendingPresenterPhoto
                                ? 'Change image'
                                : 'Upload image'}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  setFormData((prev) => ({
                                    ...prev,
                                    pendingPresenterPhoto: f || null,
                                  }));
                                  e.target.value = '';
                                }}
                              />
                            </label>
                            {(formData.presenterPhoto || formData.pendingPresenterPhoto) && (
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    presenterPhoto: '',
                                    pendingPresenterPhoto: null,
                                  }))
                                }
                                className="text-sm text-red-600 dark:text-red-400 hover:underline"
                              >
                                Remove image
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Optional. Image is saved when you save the session.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Department</label>
                    <select
                      className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100"
                      value={formData.department}
                      onChange={(e) => setFormData((prev) => ({ ...prev, department: e.target.value }))}
                    >
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full md:col-span-2">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Date</label>
                      <input
                        type="date"
                        className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100"
                        value={formData.date}
                        onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Start</label>
                      <input
                        type="time"
                        step="900"
                        className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100"
                        value={formData.startTime}
                        onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">End</label>
                      <input
                        type="time"
                        step="900"
                        className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100"
                        value={formData.endTime}
                        onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm text-gray-600 dark:text-gray-300">Attachments</label>
                    <label className="cursor-pointer px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                      + Upload file
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileSelect}
                        multiple
                      />
                    </label>
                  </div>
                  <div className="space-y-2">
                    {(formData.attachments || []).map((att, idx) => (
                      <div
                        key={`att-${idx}`}
                        className="flex items-center justify-between gap-2 py-1.5 px-2 rounded bg-gray-100 dark:bg-gray-800"
                      >
                        <a
                          href={att.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-red-600 dark:text-red-400 hover:underline truncate flex-1"
                        >
                          {att.fileName || 'Download'}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline shrink-0"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    {(formData.pendingFiles || []).map((file, idx) => (
                      <div
                        key={`pending-${idx}`}
                        className="flex items-center justify-between gap-2 py-1.5 px-2 rounded bg-gray-100 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600"
                      >
                        <span className="text-sm text-gray-700 dark:text-gray-200 truncate flex-1">
                          {file.name} (pending)
                        </span>
                        <button
                          type="button"
                          onClick={() => removePendingFile(idx)}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline shrink-0"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    {(!formData.attachments || formData.attachments.length === 0) &&
                      (!formData.pendingFiles || formData.pendingFiles.length === 0) && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        No attachments. Click &quot;Upload file&quot; to add.
                      </div>
                    )}
                  </div>
                </div>

                {saveError && (
                  <div className="text-sm text-red-600 dark:text-red-400">{saveError}</div>
                )}

                <div className="flex justify-between items-center mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.needPresenter || false}
                      onChange={(e) => setFormData((prev) => ({ ...prev, needPresenter: e.target.checked }))}
                      className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Need Presenter</span>
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setEditMode(false);
                        setFormData(null);
                        setSaveError(null);
                      }}
                      className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveSession}
                      disabled={saving}
                      className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : selectedSession.isEmpty ? 'Add Session' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BreakthroughsFulfillment;
